(() => {
  "use strict";
  function create(hooks) {
    const doc = hooks.document || document, el = hooks.elements;
    const same = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);
    const owner = () => [...(hooks.owner?.() || [])];
    const writable = () => Boolean(owner()[0]) && (!hooks.canWrite || hooks.canWrite());
    const caches = new Map();
    let identity = owner(), active = null, generation = 0;
    const name = user => user.display_name || user.handle || "Member";
    const node = (tag, cls, text) => {
      const result = doc.createElement(tag); result.className = cls || "";
      if (text != null) result.textContent = text;
      return result;
    };
    const button = (text, cls, action) => {
      const result = node("button", cls, text); result.type = "button";
      result.addEventListener("click", action); return result;
    };
    const owned = cache => same(cache.owner, owner()) && caches.get(cache.key) === cache;
    const current = panel => active === panel && panel.generation === generation
      && el.dialog.open && owned(panel.cache);
    const rowCurrent = (panel, entry) => current(panel) && panel.cache.entries.get(entry.item.user.id) === entry;
    const ownFollowers = cache => cache.direction === "followers" && cache.user.id === cache.owner[0];
    // A 4xx other than a timeout is the server's definitive refusal (for example the follow rate
    // limit); it explains why a follower remains. A lost answer has no reason to show.
    const refusal = error => {
      const status = Number(error?.status);
      const message = typeof error?.message === "string" ? error.message.trim() : "";
      return status >= 400 && status < 500 && status !== 408 && message ? message : "";
    };
    function prune() {
      for (const [key, cache] of caches) {
        if (caches.size <= 8) break;
        if (active?.cache === cache || [...cache.entries.values()].some(entry => entry.operation
          || hooks.followState.get(entry.item.user, entry.item.relationship.is_following).busy)) continue;
        caches.delete(key);
      }
    }

    function close({ navigation = false, reason = "dismiss" } = {}) {
      const panel = active;
      if (!panel) return;
      panel.cache.scroll = el.list.scrollTop; active = null; generation++;
      panel.controller?.abort();
      for (const entry of panel.cache.entries.values()) {
        entry.recovery?.abort();
        if (entry.operation?.phase === "confirm") entry.operation.confirmController.abort();
      }
      if (el.dialog.open) (hooks.closeDialog || (dialog => dialog.close()))(el.dialog);
      if (!navigation) hooks.onClose?.(reason);
    }
    function syncIdentity() {
      const next = owner();
      if (!same(identity, next)) {
        const wasOpen = Boolean(active); reset();
        if (wasOpen) hooks.onClose?.("identity");
      }
      if (active) paint(active);
    }
    function reset() {
      close({ navigation: true });
      for (const cache of caches.values()) for (const entry of cache.entries.values()) {
        entry.recovery?.abort();
        if (entry.operation?.phase === "confirm") entry.operation.confirmController.abort();
      }
      caches.clear(); identity = owner();
      el.list.textContent = ""; el.list.scrollTop = 0; el.list.setAttribute("aria-busy", "false");
      el.title.textContent = ""; el.copy.textContent = ""; el.status.textContent = "";
      el.status.hidden = true; el.empty.hidden = true; el.more.hidden = true; el.more.disabled = false;
      el.more.textContent = "Load more";
    }
    function closeMenu(panel) {
      if (!panel.menu) return false;
      const view = panel.rows.get(panel.menu); panel.menu = null;
      if (view) { view.menu.hidden = true; view.more.setAttribute("aria-expanded", "false"); }
      return true;
    }
    function makeRow(panel, entry) {
      const user = entry.item.user, row = node("div", "connections-row");
      row.dataset.userId = user.id;
      const profile = button("", "connection-person", () => {
        if (!rowCurrent(panel, entry)) return;
        const selected = entry.item.user; close({ navigation: true }); hooks.openProfile?.(selected);
      });
      const avatar = node("span", "avatar avatar-small", name(user).slice(0, 1));
      avatar.setAttribute("aria-hidden", "true"); hooks.styleAvatar?.(avatar, user);
      const copy = node("span", "connections-identity"), title = node("strong"), handle = node("small");
      copy.append(title, handle); profile.append(avatar, copy);
      const actions = node("div", "connections-actions");
      const follow = button("Follow", "secondary-button connections-follow", () => {
        if (!rowCurrent(panel, entry) || panel.cache.stale || !writable() || user.id === owner()[0]) return;
        const value = hooks.followState.get(entry.item.user, entry.item.relationship.is_following);
        if (value.busy) return;
        Promise.resolve().then(() => {
          if (!rowCurrent(panel, entry) || panel.cache.stale || !writable()) return;
          return hooks.runFollow(entry.item.user, value.following, { source: follow, current: () => current(panel) });
        })
          .catch(error => { if (rowCurrent(panel, entry)) hooks.toast?.(error.message || "Could not update follow status", true); });
      });
      const menu = node("div", "connections-menu"); menu.hidden = true;
      const more = button("…", "secondary-button connections-more", () => {
        if (!rowCurrent(panel, entry) || !ownFollowers(panel.cache)) return;
        const selected = panel.menu === user.id; closeMenu(panel);
        if (!selected) { panel.menu = user.id; menu.hidden = false; more.setAttribute("aria-expanded", "true"); remove.focus(); }
      });
      more.setAttribute("aria-expanded", "false"); more.setAttribute("aria-haspopup", "true");
      const remove = button("Remove follower", "connections-remove", () => { void removeFollower(panel, entry); });
      menu.append(remove);
      const check = button("Check status", "secondary-button connections-check", () => { void recover(panel, entry); });
      const message = node("p", "connections-row-status"); message.setAttribute("role", "status");
      actions.append(follow, more, check); row.append(profile, actions, menu, message);
      return { row, profile, title, handle, follow, more, menu, remove, check, message, entry };
    }
    function paint(panel) {
      if (!current(panel)) return;
      const cache = panel.cache;
      for (const [id, view] of panel.rows) if (!cache.entries.has(id)) { view.row.remove(); panel.rows.delete(id); }
      cache.order.forEach((id, index) => {
        const entry = cache.entries.get(id), user = entry.item.user;
        let view = panel.rows.get(id);
        if (!view || view.entry !== entry) {
          view?.row.remove(); view = makeRow(panel, entry); panel.rows.set(id, view);
        }
        view.title.textContent = name(user); view.handle.textContent = `@${user.handle}`;
        view.profile.setAttribute("aria-label", `View ${name(user)}'s profile`);
        hooks.paintFollowControl(view.follow, user, entry.item.relationship.is_following);
        view.follow.hidden = user.id === owner()[0];
        const followValue = hooks.followState.get(user, entry.item.relationship.is_following);
        view.follow.disabled = cache.stale || !writable();
        view.follow.setAttribute("aria-disabled", String(view.follow.disabled || followValue.busy));
        view.more.hidden = !ownFollowers(cache);
        view.more.setAttribute("aria-label", `More actions for ${name(user)}`);
        const disabled = cache.stale || !writable() || entry.uncertain;
        view.remove.disabled = disabled;
        view.remove.setAttribute("aria-disabled", String(disabled || Boolean(entry.operation)));
        view.remove.setAttribute("aria-label", `Remove ${name(user)} as a follower`);
        view.check.hidden = !entry.uncertain;
        view.check.disabled = cache.stale || Boolean(entry.operation);
        view.message.textContent = entry.error || (entry.operation ? "Updating…" : "");
        view.message.hidden = !view.message.textContent;
        if (el.list.children[index] !== view.row) el.list.insertBefore(view.row, el.list.children[index] || null);
      });
      el.list.setAttribute("aria-busy", String(panel.loading));
      el.empty.hidden = Boolean(cache.order.length || panel.loading || panel.error);
      el.status.hidden = !panel.loading && !panel.error;
      el.status.textContent = panel.loading ? (cache.loaded ? "Updating people…" : "Loading people…") : panel.error || "";
      el.more.hidden = panel.terminal || (!cache.nextCursor && !panel.error);
      el.more.disabled = panel.loading;
      el.more.textContent = panel.error ? "Try again" : "Load more";
    }
    function pageData(value) {
      if (!value || !Array.isArray(value.items)
        || !(value.next_cursor === null || (typeof value.next_cursor === "string" && value.next_cursor))
        || !value.items.every(item => typeof item?.user?.id === "string" && item.user.id
          && typeof item.user.handle === "string" && item.user.handle
          && typeof item.relationship?.is_following === "boolean" && typeof item.relationship?.follows_you === "boolean"
          && ["public", "private"].includes(item.profile_visibility)
          && ["open", "closed", "requested"].includes(item.viewer_access))) {
        throw new Error("The people list could not be confirmed. Please try again.");
      }
      return value;
    }
    function install(cache, items, nextCursor, append, pages, seen, version) {
      const entries = append ? new Map(cache.entries) : new Map(), order = append ? [...cache.order] : [];
      for (const item of items) {
        let entry = cache.entries.get(item.user.id);
        if (!entry) entry = { item, operation: null, recovery: null, uncertain: false, error: "" };
        entry.item = item; entries.set(item.user.id, entry);
        if (!order.includes(item.user.id)) order.push(item.user.id);
      }
      // A list-window shift is not authoritative recovery for an uncertain write.
      for (const [id, entry] of cache.entries) if ((entry.operation || entry.uncertain) && !entries.has(id)) {
        entries.set(id, entry); order.push(id);
      }
      cache.entries = entries; cache.order = order; cache.nextCursor = nextCursor;
      cache.pages = pages; cache.seen = seen; cache.loaded = true; cache.stale = false;
      for (const item of items) hooks.followState.accept(item.user, { profile: item, status: item }, version);
    }
    async function read(panel, append = false) {
      if (!current(panel) || panel.loading || (append && (!panel.cache.nextCursor || panel.cache.stale))) return;
      const cache = panel.cache, controller = new AbortController(), revision = cache.revision;
      const version = hooks.followState.version(), seen = append ? new Set(cache.seen) : new Set();
      panel.controller = controller; panel.loading = true; panel.error = ""; panel.terminal = false; panel.retryAppend = append;
      if (!append) cache.stale = true;
      paint(panel);
      let restart = false;
      try {
        let cursor = append ? cache.nextCursor : null, items = [], count = 0;
        const depth = append ? 1 : Math.max(1, cache.pages);
        do {
          const params = new URLSearchParams({ direction: cache.direction, limit: "30" });
          if (cursor) { params.set("cursor", cursor); seen.add(cursor); }
          const result = pageData(await hooks.api(`/api/follows/${encodeURIComponent(cache.user.id)}?${params}`, { signal: controller.signal }));
          if (!current(panel) || controller.signal.aborted) return;
          if (cache.revision !== revision) { restart = true; return; }
          if (result.next_cursor && seen.has(result.next_cursor)) throw new Error("The people list could not be continued. Please try again.");
          items.push(...result.items); cursor = result.next_cursor; count++;
        } while (!append && cursor && count < depth);
        install(cache, items, cursor, append, append ? cache.pages + 1 : count, seen, version);
      } catch (error) {
        if (!current(panel) || controller.signal.aborted || error.name === "AbortError") return;
        const status = Number(error.status);
        panel.terminal = status === 403 || status === 404;
        if (!panel.terminal && cache.revision !== revision) { restart = true; return; }
        if (panel.terminal) {
          cache.entries.clear(); cache.order = []; cache.nextCursor = null; cache.stale = true;
          panel.error = status === 403 ? "This profile keeps its connections private." : "This people list is no longer available.";
        } else panel.error = "People could not be loaded. Please try again.";
      } finally {
        panel.loading = false;
        if (current(panel)) {
          paint(panel); el.list.scrollTop = cache.scroll;
          if (restart) void read(panel, false);
        }
      }
    }
    function finishRemoval(panel, entry, message) {
      const cache = panel.cache, id = entry.item.user.id;
      if (!owned(cache)) return;
      const index = cache.order.indexOf(id), view = panel.rows.get(id);
      const focused = current(panel) && (view?.row.contains(doc.activeElement) || entry.focusReturn);
      entry.operation = null; entry.uncertain = false; entry.error = ""; entry.focusReturn = false;
      for (const other of caches.values()) {
        const related = other.entries.get(id);
        if (related) related.item = { ...related.item, relationship: { ...related.item.relationship, follows_you: false } };
        if (ownFollowers(other) && related) {
          other.entries.delete(id); other.order = other.order.filter(value => value !== id); other.revision++;
        }
      }
      hooks.relationshipChanged?.({ user: entry.item.user, followsYou: false });
      if (current(panel)) {
        closeMenu(panel); paint(panel);
        if (focused) {
          const next = cache.order[Math.min(index, cache.order.length - 1)];
          (panel.rows.get(next)?.profile || el.close || el.list).focus();
        }
        hooks.toast?.(message);
      } else if (active?.cache === cache && current(active)) {
        cache.stale = true; paint(active); void read(active);
      }
    }
    async function recover(panel, entry, prior = null, refused = "") {
      const cache = panel.cache;
      if (!owned(cache) || (!prior && (!rowCurrent(panel, entry) || entry.operation))) return;
      const operation = prior || { phase: "recovery" };
      entry.operation = operation; entry.uncertain = true;
      const controller = new AbortController(); entry.recovery = controller;
      if (current(panel)) paint(panel);
      try {
        const result = await hooks.api(`/api/profiles/${encodeURIComponent(entry.item.user.handle)}`, { signal: controller.signal });
        if (!owned(cache) || entry.operation !== operation || controller.signal.aborted) return;
        if (result?.user?.id !== entry.item.user.id || typeof result.relationship?.follows_you !== "boolean") throw new Error("Unconfirmed relationship");
        if (!result.relationship.follows_you) { finishRemoval(panel, entry, "No longer follows you"); return; }
        entry.uncertain = false;
        entry.error = refused ? `${refused} Still follows you.` : "Still follows you. You can try removing this follower again.";
      } catch (_) {
        if (owned(cache) && entry.operation === operation) {
          entry.uncertain = true; entry.error = "Could not confirm this follower. Check status before trying again.";
        }
      } finally {
        if (entry.operation === operation) entry.operation = null;
        if (entry.recovery === controller) entry.recovery = null;
        if (current(panel)) paint(panel);
        else if (active?.cache === cache && current(active)) paint(active);
        prune();
      }
    }
    async function removeFollower(panel, entry) {
      const cache = panel.cache;
      if (!rowCurrent(panel, entry) || !ownFollowers(cache) || cache.stale || !writable() || entry.operation || entry.uncertain) return;
      const operation = { phase: "confirm", confirmController: new AbortController() }; entry.operation = operation;
      const view = panel.rows.get(entry.item.user.id); entry.focusReturn = Boolean(view?.row.contains(doc.activeElement));
      closeMenu(panel); paint(panel);
      let submitted = false;
      try {
        const confirmed = await hooks.confirm(`Remove ${name(entry.item.user)} as a follower?`, {
          title: "Remove follower?", body: `@${entry.item.user.handle} will no longer follow you.`, confirmLabel: "Remove", destructive: true,
          signal: operation.confirmController.signal,
        });
        if (!confirmed || !rowCurrent(panel, entry) || !writable() || cache.stale || entry.operation !== operation) return;
        submitted = true; operation.phase = "write";
        const result = await hooks.api(`/api/followers/${encodeURIComponent(entry.item.user.id)}`, { method: "DELETE", csrf: true });
        if (!owned(cache) || entry.operation !== operation) return;
        if (typeof result?.removed !== "boolean") throw new Error("Unconfirmed removal");
        finishRemoval(panel, entry, result.removed ? "Follower removed" : "No longer follows you");
      } catch (error) {
        if (submitted && owned(cache) && entry.operation === operation) {
          entry.uncertain = true;
          if (current(panel)) await recover(panel, entry, operation, refusal(error));
          else entry.error = "Could not confirm this follower. Check status before trying again.";
        }
      } finally {
        if (entry.operation === operation) entry.operation = null;
        if (current(panel)) {
          paint(panel);
          if (!submitted && entry.focusReturn) panel.rows.get(entry.item.user.id)?.more.focus();
        }
        if (!submitted) entry.focusReturn = false;
        prune();
      }
    }
    function repaintFollow(author, reason) {
      if (!author?.id) return;
      syncIdentity();
      const value = hooks.followState.get(author);
      let focusIndex = null;
      let membershipChanged = false;
      const confirmed = ["current", "reconciled"].includes(reason) && value.known && !value.busy && !value.uncertain;
      if (confirmed) {
        for (const cache of caches.values()) if (cache.direction === "following" && cache.user.id === cache.owner[0]) {
          // An older page may include an unfollowed author or omit a new follow.
          cache.revision++;
          if (!value.following && cache.entries.has(author.id)) {
            if (active?.cache === cache && active.rows.get(author.id)?.row.contains(doc.activeElement)) focusIndex = cache.order.indexOf(author.id);
            cache.entries.delete(author.id); cache.order = cache.order.filter(id => id !== author.id);
            membershipChanged = true;
          }
        }
      }
      if (active) {
        paint(active);
        if (focusIndex !== null) {
          const id = active.cache.order[Math.min(focusIndex, active.cache.order.length - 1)];
          (active.rows.get(id)?.profile || el.close || el.list).focus();
        }
      }
      if (confirmed && value.following && active && current(active)
        && active.cache.direction === "following" && active.cache.user.id === active.cache.owner[0]) {
        active.cache.stale = true; paint(active); void read(active);
      }
      if (confirmed && (membershipChanged || (active && current(active) && active.cache.user.id === active.cache.owner[0]))) {
        hooks.relationshipChanged?.({ user: author, following: value.following });
      }
      prune();
    }
    function open({ profile, direction }) {
      syncIdentity();
      if (!profile?.user?.id || !profile.user.handle || !["followers", "following"].includes(direction)) return false;
      if (active && active.cache.user.id === profile.user.id && active.cache.direction === direction) return true;
      close({ navigation: true });
      const key = `${profile.user.id}:${direction}`;
      let cache = caches.get(key);
      if (!cache) {
        cache = { key, owner: [...identity], user: profile.user, direction, entries: new Map(), order: [], nextCursor: null,
          pages: 0, loaded: false, stale: true, scroll: 0, seen: new Set(), revision: 0 };
        caches.set(key, cache);
      } else { cache.user = profile.user; caches.delete(key); caches.set(key, cache); }
      const panel = { cache, generation: ++generation, rows: new Map(), menu: null, loading: false, error: "", terminal: false };
      active = panel; prune(); el.list.textContent = ""; el.dialog.dataset.noHistory = "true";
      el.title.textContent = direction === "followers" ? "Followers" : "Following";
      el.copy.textContent = `Accounts ${direction === "followers" ? "following" : "followed by"} @${profile.user.handle}.`;
      (hooks.openDialog || (dialog => dialog.showModal()))(el.dialog);
      paint(panel); el.list.scrollTop = cache.scroll; void read(panel); return true;
    }
    el.list.tabIndex = -1;
    el.close?.addEventListener("click", () => close());
    el.more.addEventListener("click", () => { if (active) void read(active, active.error ? active.retryAppend : true); });
    el.list.addEventListener("scroll", () => { if (active) active.cache.scroll = el.list.scrollTop; });
    el.dialog.addEventListener("cancel", event => {
      event.preventDefault();
      if (active?.menu) {
        const view = active.rows.get(active.menu); closeMenu(active); view?.more.focus();
      } else close();
    });
    el.dialog.addEventListener("close", () => { if (active && !el.dialog.open) close(); });
    return Object.freeze({ open, close, reset, syncIdentity, syncCapabilities: () => { syncIdentity(); }, repaintFollow,
      target: () => active ? { user: active.cache.user, direction: active.cache.direction } : null,
      isOpenFor: (id, direction) => Boolean(active && current(active) && active.cache.user.id === id && active.cache.direction === direction) });
  }
  if (typeof module !== "undefined" && module.exports) module.exports = { create };
  else window.LouderrConnections = Object.freeze({ create });
})();
